import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Box, Button, makeStyles, useMediaQuery, useTheme } from '@material-ui/core'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity } from '../../../../api'
import { ContractModel } from '../../../../api/Argentia/model/contracts'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { getCustomerDomain } from '../../../../redux'
import { fetchSubjects } from '../../../../services/subjects.service'
import { fetchContractsBySubjectID } from '../../../contracts/contracts.service'
import { mapContractsToSelectOptions } from '../../../procedures/procedures.mapper'
import { EnumFloatingConceptsTabs } from '../../floatingConcepts.model'
import { styles } from '../../floatingConcepts.styles'

interface FloatingConceptsSearchFormProps {
  activeTab: EnumFloatingConceptsTabs
  commodity: EnumCommodity
  onFormClear: () => void
  onFormHasValue: (hasValue: boolean) => void
  onSearchAllButton: (searchFormData: FloatingConceptsSearchForm) => Promise<void>
  onFilterLastButton: (searchFormData: FloatingConceptsSearchForm) => void
  settlementGroupOptionsList?: SelectOption[]
  isLoadingSettlementGroupsOptions?: boolean
}

interface FormDependencies {
  subject: boolean
  contract: boolean
  settlementGroup: boolean
}

const INITIAL_FORM_DEPENDENCIES: FormDependencies = {
  subject: false,
  contract: false,
  settlementGroup: false
}

export interface FloatingConceptsSearchForm {
  subject?: SelectOption | undefined
  contract?: number
  settlementGroupId?: number | undefined
  startDate: Date | undefined
  endDate: Date | undefined
}

export const FloatingConceptsSearchForm: React.FC<FloatingConceptsSearchFormProps> = ({
  activeTab,
  commodity,
  onFormClear,
  onFormHasValue,
  onSearchAllButton,
  onFilterLastButton,
  settlementGroupOptionsList,
  isLoadingSettlementGroupsOptions
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'floatingConcepts'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { info, error } = useNotify()
  const [searchFormData, setSearchFormData] = useState<FloatingConceptsSearchForm>(
    {} as FloatingConceptsSearchForm
  )
  const [subjectContracts, setSubjectContracts] = useState<ContractModel[]>([])
  const [formDependencies, setFormDependencies] =
    useState<FormDependencies>(INITIAL_FORM_DEPENDENCIES)
  const [isLoadingContracts, setIsLoadingContracts] = useState<boolean>(false)
  const [formIsCompleted, setFormIsCompleted] = useState<boolean>(false)
  const [resetTrigger, setResetTrigger] = useState<number>(0)
  const isMd = useMediaQuery(currentTheme.breakpoints.down('md'))

  const isFloatingConceptsTab =
    activeTab !== EnumFloatingConceptsTabs.virtualBattery &&
    activeTab !== EnumFloatingConceptsTabs.balanceActions
  const isBalanceActionsTab = activeTab === EnumFloatingConceptsTabs.balanceActions
  // const isRightsTab = activeTab === EnumFloatingConceptsTabs.rights

  // #region Handlers Starts
  const handleFormDependencies = (field: keyof FormDependencies, value: boolean) => {
    setFormDependencies({
      ...formDependencies,
      [field]: value
    })
  }

  const validateForm = (formState: FloatingConceptsSearchForm): boolean => {
    console.log(activeTab)
    switch (activeTab) {
      case EnumFloatingConceptsTabs.distribucion:
      case EnumFloatingConceptsTabs.refacturacion:
      case EnumFloatingConceptsTabs.penalizacion:
      case EnumFloatingConceptsTabs.manual:
        return !!(formState.subject && formState.contract)
      case EnumFloatingConceptsTabs.balanceActions:
        return !!formState.settlementGroupId
      default:
        return false
    }
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  const getSubjectContracts = subjectId => {
    setIsLoadingContracts(true)
    fetchContractsBySubjectID(subjectId).then(response => {
      if (response.success) {
        response.data && setSubjectContracts(response.data)
      } else {
        error(t('floatingConcepts:notifications.getSubjectContracts.error'))
      }
      setIsLoadingContracts(false)
    })
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  const searchFormOptions: FormOptions = {
    // elevation: 1,
    columns: isMd ? 2 : 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('floatingConcepts:components.floatingConceptsDefaultTab.searchForm.analytics')
    }
  }

  const searchFormSection: Section[] = [
    {
      inputs: [
        {
          name: 'subject',
          display: isFloatingConceptsTab,
          required: isFloatingConceptsTab,
          label: t(
            'floatingConcepts:components.floatingConceptsDefaultTab.searchForm.inputs.subject'
          ),
          type: DataType.SEARCHFIELD,
          placeholder: t(
            'floatingConcepts:components.floatingConceptsDefaultTab.searchForm.placeholders.subject'
          ),
          apiCall: fetchSubjects,
          onChange: (subjectSelected: any, formState: any) => {
            if (subjectSelected) {
              //clean previous contracts from FormData
              setSearchFormData({
                ...formState,
                contract: null
              })
              //update form MULTISELECT values if field is not Null
              getSubjectContracts(subjectSelected.value)
            } else {
              setSubjectContracts([])
              setSearchFormData({
                ...formState,
                contract: null
              })
              onFormClear()
            }
            setFormIsCompleted(false)
          },
          clearable: true
        },
        {
          name: 'contract',
          display: isFloatingConceptsTab,
          required: isFloatingConceptsTab,
          label: t(
            'floatingConcepts:components.floatingConceptsDefaultTab.searchForm.inputs.contract'
          ),
          type: DataType.SELECT,
          isLoading: isLoadingContracts,
          selectOptions: mapContractsToSelectOptions(subjectContracts),
          onChange: (value, formState) => {
            if (value) {
              handleFormDependencies('contract', true)
            } else {
              handleFormDependencies('contract', false)
            }
            setFormIsCompleted(validateForm(formState as FloatingConceptsSearchForm)) // helps component to enable/disable search button
            onFormHasValue(validateForm(formState as FloatingConceptsSearchForm)) // helps father component know if form has value
          }
        },
        {
          name: 'settlementGroupId',
          display: isBalanceActionsTab,
          required: isBalanceActionsTab,
          label: t(
            'floatingConcepts:components.floatingConceptsDefaultTab.searchForm.inputs.settlementGroup'
          ),
          type: DataType.SELECT,
          selectOptions: settlementGroupOptionsList ?? [],
          isLoading: isLoadingSettlementGroupsOptions ?? false,
          onChange: (value, formState) => {
            if (value) {
              handleFormDependencies('settlementGroup', true)
            } else {
              handleFormDependencies('settlementGroup', false)
            }
            setFormIsCompleted(validateForm(formState as FloatingConceptsSearchForm)) // helps component to enable/disable search button
            onFormHasValue(validateForm(formState as FloatingConceptsSearchForm)) // helps father component know if form has value
          }
        },
        {
          name: 'startDate',
          label: t(
            'floatingConcepts:components.floatingConceptsDefaultTab.searchForm.inputs.startDate'
          ),
          type: DataType.DATE,
          clearable: true
        },
        {
          name: 'endDate',
          label: t(
            'floatingConcepts:components.floatingConceptsDefaultTab.searchForm.inputs.endDate'
          ),
          type: DataType.DATE,
          clearable: true
        }
      ]
    }
  ]

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // Clear search form when tab or commodity changes
  React.useEffect(() => {
    // Increment reset trigger to force Form remount
    setResetTrigger(prev => prev + 1)
    // Reset all form state
    setSearchFormData({
      subject: null,
      contract: null,
      settlementGroupId: null,
      startDate: undefined,
      endDate: undefined
    } as any)
    setSubjectContracts([])
    setFormDependencies(INITIAL_FORM_DEPENDENCIES)
    setFormIsCompleted(false)
    onFormClear()
  }, [activeTab, commodity])
  // #endregion Hooks Ends

  return (
    <CardWithTransition show={true} raised={true}>
      <Form
        key={`${activeTab}-${commodity}-${resetTrigger}`}
        title={t('floatingConcepts:components.floatingConceptsDefaultTab.searchForm.title')}
        options={searchFormOptions}
        sections={searchFormSection}
        data={searchFormData}
      />
      <Box className={classes.cardFooterButtons}>
        <Button
          size="small"
          variant="text"
          color="primary"
          onClick={() => {
            onFilterLastButton(searchFormData)
          }}
        >
          {t('floatingConcepts:components.floatingConceptsDefaultTab.searchForm.buttons.last1000')}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={() => {
            onSearchAllButton(searchFormData)
          }}
          disabled={!formIsCompleted}
        >
          {t('floatingConcepts:components.floatingConceptsDefaultTab.searchForm.buttons.main')}
        </Button>
      </Box>
    </CardWithTransition>
  )
}
